import { supabase } from '../supabase';

type ErrorLogParams = {
  bot_id?: string;
  user_id?: string;
  error_type: 'WEBHOOK' | 'ORDER' | 'EXCHANGE' | 'MARKET' | 'STATS' | 'STRATEGY' | 'ENGINE' | 'BALANCE' | 'TRAILING_STOP';
  error_message: string;
  error_details?: any;
  alert_id?: string;
  order_id?: string;
};

// Helper function to safely stringify error details
function safeStringifyError(error: any): string {
  if (!error) return 'No error details available';
  
  try {
    if (error instanceof Error) {
      return error.message || error.toString();
    }
    if (typeof error === 'string') {
      return error;
    }
    return JSON.stringify(error);
  } catch {
    return 'Error details could not be stringified';
  }
}

export async function logError(params: ErrorLogParams) {
  try {
    // Create error log entry
    const { data, error } = await supabase
      .from('error_logs')
      .insert({
        bot_id: params.bot_id,
        user_id: params.user_id,
        error_type: params.error_type,
        error_message: params.error_message,
        error_details: params.error_details,
        alert_id: params.alert_id,
        order_id: params.order_id,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to log error:', error);
      return null;
    }

    // Update bot status if bot_id and user_id provided
    if (params.user_id && params.bot_id) {
      try {
        // First get incremented error count
        const { data: countData, error: countError } = await supabase
          .rpc('increment_error_count', {
            p_bot_id: params.bot_id,
            p_user_id: params.user_id
          });

        if (countError) throw countError;

        // Then update bot status with new count
        const { error: statusError } = await supabase
          .from('user_bot_status')
          .update({
            error_count: countData || 1,
            last_error: params.error_message,
            updated_at: new Date().toISOString()
          })
          .eq('bot_id', params.bot_id)
          .eq('user_id', params.user_id);

        if (statusError) throw statusError;
      } catch (updateError) {
        console.error('Failed to update bot status:', updateError);
      }
    }

    return data;
  } catch (error) {
    console.error('Error logging error:', safeStringifyError(error));
    return null;
  }
}

// Add error recovery function
export async function recoverFromError(error: any, botId: string, userId: string) {
  try {
    // Log the error
    await logError({
      bot_id: botId,
      user_id: userId,
      error_type: 'ORDER',
      error_message: error?.message || safeStringifyError(error),
      error_details: error
    });

    // Update bot status
    const { error: statusError } = await supabase
      .from('user_bot_status')
      .update({ 
        error_count: supabase.rpc('increment_error_count', {
          p_bot_id: botId,
          p_user_id: userId
        }),
        last_error: error?.message || safeStringifyError(error),
        updated_at: new Date().toISOString()
      })
      .eq('bot_id', botId)
      .eq('user_id', userId);

    if (statusError) {
      console.error('Failed to update bot status:', statusError);
    }

    // Return fallback data
    return {
      pnl_percentage: 0,
      unrealized_pnl: 0,
      positions: [],
      error: error?.message || safeStringifyError(error)
    };
  } catch (recoverError) {
    console.error('Error in error recovery:', safeStringifyError(recoverError));
    return null;
  }
}
